import { useState, useEffect } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCreationRequestDetail } from '../../hooks/useCreationRequests';
import { REQUEST_STATUS, REQUEST_STATUS_LABELS, REQUEST_STATUS_ORDER } from '../../services/creationRequestService';
import { getUserPreviewInfo } from '../../services/previewService';
import { formatRelativeDate } from '../../utils/relativeTime';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from './StatusBadge';
import PreviewsManager from './PreviewsManager';
import DomainSuggestionsManager from './DomainSuggestionsManager';
import RequestMessages from './RequestMessages';
import RequestInfo from './RequestInfo';

/**
 * Détail d'une demande de création assistée :
 * statut, prévisualisations, dialogue centralisé, infos du formulaire.
 * Les emails au client sont envoyés automatiquement par le trigger
 * onCreationRequestStatusChange (confirmation, création, site prêt).
 */
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://portail.monsitedirect.fr';

const CreationRequestDetail = ({ userId, onBack }) => {
  const { currentUser } = useAuthContext();

  const {
    request,
    isLoading,
    isError,
    error,
    updateStatus,
    isUpdatingStatus,
    addPreview,
    isAddingPreview,
    removePreview,
    setFinalPreviewUrl,
    addDomainSuggestion,
    removeDomainSuggestion,
    isManagingSuggestions,
    sendMessage,
    isSendingMessage,
    sendStaffEmail,
    isSendingStaffEmail,
    sendFeedback,
    isSendingFeedback
  } = useCreationRequestDetail(userId);

  const { markRequestNotifsReadForUser } = useNotifications();

  // À l'ouverture du détail : la réponse client est considérée comme lue
  // (la pastille est retirée sur cette demande uniquement).
  useEffect(() => {
    if (userId) {
      markRequestNotifsReadForUser(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const [previewInfo, setPreviewInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getUserPreviewInfo(userId).then((info) => {
      if (!cancelled) setPreviewInfo(info);
    });
    return () => { cancelled = true; };
  }, [userId]);

  if (isLoading) {
    return (
      <div className="py-10 text-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (isError) {
    return <div className="py-10 text-center text-error">Erreur : {error?.message}</div>;
  }

  if (!request) {
    return <div className="py-10 text-center text-base-content/60">Demande introuvable.</div>;
  }

  const staffName = currentUser?.displayName || 'Admin';

  const handleStatusChange = (e) => {
    updateStatus(e.target.value);
  };

  /** Email manuel : notifie le client qu'un message de l'équipe l'attend (chat) */
  const handleSendStaffEmail = () => {
    sendStaffEmail(undefined, {
      onSuccess: () => toast.success("Email de notification envoyé au client."),
      onError: (err) => toast.error(err?.message || "Impossible d'envoyer l'email.")
    });
  };

  /** Email manuel : demande d'avis (feedback) au client */
  const handleSendFeedback = () => {
    sendFeedback(undefined, {
      onSuccess: () => toast.success("Email de demande d'avis envoyé au client."),
      onError: (err) => toast.error(err?.message || "Impossible d'envoyer l'email.")
    });
  };

  return (
    <div className="space-y-6">
      <button type="button" onClick={onBack} className="btn btn-ghost btn-sm">
        ← Retour à la liste
      </button>

      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">{request.userInfo?.displayName}</h2>
          <p className="text-sm text-base-content/60">
            {request.userInfo?.email} · {request.businessInfo?.nomCommercial || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={request.status} />
          <Link
            to={`/clients/${request.userId}`}
            className="btn btn-sm btn-outline"
            title="Voir la fiche client consolidée (site, création, support)"
          >
            🗂 Fiche client
          </Link>
          <a
            href={`${PORTAL_URL}/admin/assist?uid=${request.userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline"
            title="Ouvrir le portail en mode assistance (agir comme cet utilisateur)"
          >
            Démarrer le mode assistance
          </a>
        </div>
      </div>

      {/* Statut + emails automatiques */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label"><span className="label-text">Statut de la demande</span></label>
          <select
            className="select select-bordered w-full"
            value={request.status}
            onChange={handleStatusChange}
            disabled={isUpdatingStatus}
          >
            {REQUEST_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{REQUEST_STATUS_LABELS[s]}</option>
            ))}
          </select>
          {request.status === REQUEST_STATUS.SITE_PUBLIE && (
            <p className="text-xs text-success mt-1">✅ Site publié confirmé par l'équipe.</p>
          )}
          {request.trackingClosed && request.status !== REQUEST_STATUS.SITE_PUBLIE && (
            <p className="text-xs text-base-content/60 mt-1">
              {request.closedBy === 'client'
                ? '✓ Clôturé par le client — il a pris le relais sur son tableau de bord.'
                : 'Suivi fermé.'}
            </p>
          )}
        </div>
        <div className="flex items-end">
          <div className="w-full bg-base-200 border border-base-300 rounded-lg p-3 text-sm text-base-content/70">
            📧 Les emails au client (confirmation de demande, création en cours, site prêt)
            sont envoyés <strong>automatiquement</strong> à chaque changement de statut.
          </div>
        </div>
      </div>

      {/* Prévisualisations */}
      <div className="card bg-base-100 border border-base-200">
        <div className="card-body">
          <h3 className="font-semibold mb-2">Prévisualisations</h3>
          <PreviewsManager
            previews={request.previews}
            finalPreviewUrl={request.finalPreviewUrl}
            finalPreviewId={request.finalPreviewId}
            onAdd={addPreview}
            onRemove={removePreview}
            onSetFinal={setFinalPreviewUrl}
            isAdding={isAddingPreview}
          />
        </div>
      </div>

      {/* Domaines recommandés (proposés par l'équipe, affichés au client à site_pret) */}
      <div className="card bg-base-100 border border-base-200">
        <div className="card-body">
          <h3 className="font-semibold mb-2">Domaines recommandés</h3>
          <DomainSuggestionsManager
            suggestions={request.domainSuggestions || []}
            domainWish={request.domainWish}
            isManaging={isManagingSuggestions}
            onAdd={(suggestion) =>
              addDomainSuggestion(suggestion, {
                onSuccess: () => toast.success('Domaine recommandé ajouté.'),
                onError: (err) => toast.error(err?.message || "Impossible d'ajouter le domaine.")
              })
            }
            onRemove={(index) =>
              removeDomainSuggestion(index, {
                onSuccess: () => toast.success('Domaine recommandé retiré.'),
                onError: (err) => toast.error(err?.message || 'Impossible de retirer le domaine.')
              })
            }
          />
        </div>
      </div>

      {/* Prévisualisation générée (deployment-service) */}
      <div className="card bg-base-100 border border-base-200">
        <div className="card-body">
          <h3 className="font-semibold mb-2">Prévisualisation générée</h3>
          {previewInfo?.previewUrl ? (
            <div className="flex flex-wrap items-center gap-3">
              <a href={previewInfo.previewUrl} target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-outline btn-info">
                👁 Voir la prévisualisation
              </a>
              <span
                className="text-sm text-base-content/60"
                title={previewInfo.previewedAt ? new Date(previewInfo.previewedAt).toLocaleString('fr-FR') : ''}
              >
                {previewInfo.previewedAt
                  ? `Dernière prévisualisation : ${formatRelativeDate(previewInfo.previewedAt)}`
                  : ''}
              </span>
            </div>
          ) : (
            <p className="text-sm text-base-content/60">Aucune prévisualisation générée récemment.</p>
          )}
        </div>
      </div>

      {/* Dialogue */}
      <div className="card bg-base-100 border border-base-200">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="font-semibold">Dialogue avec le client</h3>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={handleSendStaffEmail}
                disabled={isSendingStaffEmail}
                title="Envoyer au client un email l'informant qu'un message de l'équipe l'attend"
              >
                {isSendingStaffEmail ? <span className="loading loading-spinner loading-xs"></span> : '📧'} Notifier par email
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={handleSendFeedback}
                disabled={isSendingFeedback}
                title="Envoyer au client un email de demande d'avis (page de satisfaction)"
              >
                {isSendingFeedback ? <span className="loading loading-spinner loading-xs"></span> : '⭐'} Mail feedback
              </button>
            </div>
          </div>
          <RequestMessages
            messages={request.messages}
            onSend={(msg) => sendMessage({ ...msg, staffName })}
            isSending={isSendingMessage}
          />
        </div>
      </div>

      {/* Informations du formulaire */}
      <div className="card bg-base-100 border border-base-200">
        <div className="card-body">
          <h3 className="font-semibold mb-2">Informations de la demande</h3>
          <RequestInfo request={request} />
        </div>
      </div>
    </div>
  );
};

export default CreationRequestDetail;
